/*!
 * mm-timeline.js — the mashup's timeline: bars ruler, the vocal block over the
 * music, a playhead. The person moves the vocal and trims it here:
 *   • a drag of the block moves it — by bars, by beats or freely: the page decides
 *     (`snap`), the timeline draws what the page answers and reports the raw distance;
 *   • a drag of a block's EDGE trims the vocal — the page snaps it to the singer's bars;
 *   • a drag of a grip on the music lane sets where the mashup file starts or ends (`range`);
 *   • ←/→ move by a bar, Shift+←/→ by a beat, Alt+←/→ by 10 ms; a click on the music seeks.
 *
 *   var tl = MashupTimeline.create(canvas, { onSeek(t), onSelect(which), label,
 *            snap(dt, which, kind) → dt to draw (kind: 'move' | 'trim0' | 'trim1' | 'r0' | 'r1'),
 *            onMove({ which, dt, final }), onTrim({ which, edge, dt }), onKey({ which, bars, beats, fine }),
 *            onRange({ edge, dt }) — the mashup's own edges, dragged on the music lane })
 *   tl.set({ duration, bars:[t…], beats:[t…], music: AudioBuffer|null, vocal: {buffer, at}|null, startBar,
 *            vocal2: {buffer, at}|null, startBar2, active: 'a'|'c', range: [t0, t1]|null })
 *   tl.playhead(t); tl.zoom(+1|-1); tl.destroy()
 *
 * The canvas is a focusable role="slider" whose value is the bar the ACTIVE vocal
 * starts on, so the placement is reachable and announced without a mouse. A
 * three-song mashup has a second vocal lane (C): a drag moves the block under the
 * pointer, a click on a block makes it the active one, the keys move the active one.
 */
(function (root) {
    'use strict';

    function cssVar(el, name, fb) { try { var v = getComputedStyle(el).getPropertyValue(name).trim(); return v || fb; } catch (_) { return fb; } }

    /** min/max per column over all channels, [min0,max0,min1,max1,…] — its own, so the format never drifts.
     *  `buf.sum` — several buffers heard together (the music's stems): their first channels are summed
     *  here, per sampled point. The page used to build the full-length sum just to draw it — 57 MB for a
     *  5-min song on a phone that was already out of memory (bug report 2026-09-28). */
    function peaksOf(buf, cols) {
        if (!buf) return null;
        var n = buf.length, out = new Float32Array(cols * 2), step = n / cols;
        var stride = Math.max(1, Math.floor(step / 256)), c, a, b, mn, mx, i, v;
        if (buf.clips) {
            // the music as the timeline plays it — clips of its stems ({t0, t1, off}, MashupMixer.clipsOf),
            // the body going round under a long vocal: point i of the TIMELINE read where its clip is
            var cl = buf.clips, src = (buf.sum || [buf.buffer]).map(function (x) { return x.getChannelData(0); }), ns = src.length;
            var sr = buf.sampleRate, ci = 0;
            for (c = 0; c < cols; c++) {
                a = Math.floor(c * step); b = Math.min(n, Math.floor((c + 1) * step)); mn = 0; mx = 0;
                for (i = a; i < b; i += stride) {
                    var t = i / sr;
                    while (ci < cl.length - 1 && t >= cl[ci].t1) ci++;
                    var q = cl[ci]; if (!q || t < q.t0 || t >= q.t1) continue;
                    var k = Math.round((q.off + t - q.t0) * sr); v = 0;
                    for (var p0 = 0; p0 < ns; p0++) if (k >= 0 && k < src[p0].length) v += src[p0][k];
                    if (v < mn) mn = v; if (v > mx) mx = v;
                }
                out[c * 2] = mn; out[c * 2 + 1] = mx;
            }
            return out;
        }
        if (buf.sum) {
            var parts = buf.sum.map(function (x) { return x.getChannelData(0); }), np = parts.length;
            for (c = 0; c < cols; c++) {
                a = Math.floor(c * step); b = Math.min(n, Math.floor((c + 1) * step)); mn = 0; mx = 0;
                for (i = a; i < b; i += stride) { v = 0; for (var p = 0; p < np; p++) { var d0 = parts[p]; if (i < d0.length) v += d0[i]; } if (v < mn) mn = v; if (v > mx) mx = v; }
                out[c * 2] = mn; out[c * 2 + 1] = mx;
            }
            return out;
        }
        var nc = Math.min(2, buf.numberOfChannels || 1), chans = [];
        for (var c0 = 0; c0 < nc; c0++) chans.push(buf.getChannelData(c0));
        for (c = 0; c < cols; c++) {
            a = Math.floor(c * step); b = Math.min(n, Math.floor((c + 1) * step)); mn = 0; mx = 0;
            for (var k = 0; k < nc; k++) { var d = chans[k]; for (i = a; i < b; i += stride) { v = d[i]; if (v < mn) mn = v; if (v > mx) mx = v; } }
            out[c * 2] = mn; out[c * 2 + 1] = mx;
        }
        return out;
    }
    function peakPair(p, i) { return p ? [p[i * 2] || 0, p[i * 2 + 1] || 0] : [0, 0]; }

    function create(canvas, opts) {
        opts = opts || {};
        var ctx = canvas.getContext('2d'), dpr = Math.max(1, Math.min(3, root.devicePixelRatio || 1));
        // `range` — [t0, t1] of the mashup the file takes (the person's edges of the music), or null: all of it
        var S = { duration: 1, bars: [], beats: [], music: null, seams: [], vocal: null, startBar: 0, vocal2: null, startBar2: 0, active: 'a', range: null };
        var view = { t0: 0, t1: 1 }, ph = 0, W = 0, H = 0;
        var musicPeaks = null, vocalPeaks = null, vocal2Peaks = null, peakCols = 0;
        var drag = null, raf = 0;
        var RULER = 22, GAP = 6;

        canvas.tabIndex = 0;
        canvas.setAttribute('role', 'slider');
        if (opts.label) canvas.setAttribute('aria-label', opts.label);

        function lanes() {
            var h = H - RULER;
            if (S.vocal2 && S.vocal2.buffer) {
                var l3 = Math.round(h * 0.31);
                return { vocal: { y: RULER + 2, h: l3 - GAP }, vocal2: { y: RULER + 2 + l3, h: l3 - GAP }, music: { y: RULER + 2 + 2 * l3, h: h - 2 * l3 - 4 } };
            }
            var lv = Math.round(h * 0.46);
            return { vocal: { y: RULER + 2, h: lv - GAP }, music: { y: RULER + 2 + lv, h: h - lv - 4 } };
        }
        /** The vocal block of a drag kind: 'vocal' (A) or 'vocal2' (C). */
        function blockOf(kind) { return kind === 'vocal2' ? S.vocal2 : S.vocal; }
        /** Which vocal block is under a point, or null. */
        function hitBlock(px, py) { var h = hitPart(px, py); return h ? h.kind : null; }
        // the grip of an edge: wider under a finger. A block too narrow for two grips and a body is moved, not trimmed
        function edgeGrip() { try { return root.matchMedia && root.matchMedia('(pointer: coarse)').matches ? 14 : 7; } catch (_) { return 7; } }
        /** The vocal block under a point and the part of it: { kind: 'vocal'|'vocal2', part: 'body'|'trim0'|'trim1' }. */
        /** The two edges of the mashup on the music lane: the person's range, or the music's own span. */
        function rangeEdges() { var end = S.music ? S.music.duration : S.duration; return S.range ? [S.range[0], S.range[1]] : [0, end]; }
        function hitPart(px, py) {
            var L = lanes(), out = null, g = edgeGrip();
            if (opts.onRange && S.music && py >= L.music.y && py <= L.music.y + L.music.h) {
                var re = rangeEdges();
                if (Math.abs(px - x(re[0])) <= g + 2) return { kind: 'range', part: 'r0' };
                if (Math.abs(px - x(re[1])) <= g + 2) return { kind: 'range', part: 'r1' };
            }
            ['vocal', 'vocal2'].forEach(function (kind) {
                var V = blockOf(kind), ln = L[kind];
                if (out || !V || !V.buffer || !ln || py < ln.y || py > ln.y + ln.h) return;
                var xa = x(V.at), xb = x(V.at + V.buffer.duration);
                if (px < xa - g || px > xb + g) return;
                var room = xb - xa >= 4 * g;
                out = { kind: kind, part: room && Math.abs(px - xa) <= g ? 'trim0' : room && Math.abs(px - xb) <= g ? 'trim1' : px >= xa && px <= xb ? 'body' : null };
                if (!out.part) out = null;
            });
            return out;
        }
        function x(t) { return (t - view.t0) / (view.t1 - view.t0) * W; }
        function tAt(px) { return view.t0 + px / W * (view.t1 - view.t0); }

        function resize() {
            var r = canvas.getBoundingClientRect();
            var w = Math.max(200, Math.round(r.width)), h = Math.max(120, Math.round(r.height || 160));
            if (w === W && h === H) return;
            W = w; H = h;
            canvas.width = W * dpr; canvas.height = H * dpr;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            buildPeaks(); draw();
        }
        function buildPeaks() {
            // peaks at the WHOLE timeline resolution × zoom headroom (cheap to redo on zoom)
            var span = view.t1 - view.t0, cols = Math.max(200, Math.min(12000, Math.round(W * S.duration / Math.max(0.5, span))));
            if (cols === peakCols && musicPeaks) return;
            peakCols = cols;
            musicPeaks = S.music ? peaksOf(S.music, cols) : null;
            vocalPeaks = S.vocal && S.vocal.buffer ? peaksOf(S.vocal.buffer, Math.max(50, Math.round(cols * S.vocal.buffer.duration / Math.max(1, S.duration)))) : null;
            vocal2Peaks = S.vocal2 && S.vocal2.buffer ? peaksOf(S.vocal2.buffer, Math.max(50, Math.round(cols * S.vocal2.buffer.duration / Math.max(1, S.duration)))) : null;
        }

        function wave(peaks, cols, t0, dur, lane, color) {
            if (!peaks) return;
            ctx.fillStyle = color;
            var mid = lane.y + lane.h / 2, amp = lane.h / 2 - 2;
            var xa = Math.max(0, Math.floor(x(t0))), xb = Math.min(W, Math.ceil(x(t0 + dur)));
            for (var px = xa; px < xb; px++) {
                var t = tAt(px + 0.5), i = Math.floor((t - t0) / dur * cols);
                if (i < 0 || i >= cols) continue;
                var pp = peakPair(peaks, i), mn = Math.max(-1, pp[0]), mx = Math.min(1, pp[1]);
                ctx.fillRect(px, mid - mx * amp, 1, Math.max(1, (mx - mn) * amp));
            }
        }

        function draw() {
            if (!W) return;
            var fg = cssVar(canvas, '--lg-text-2', '#9aa4b2'), dim = cssVar(canvas, '--lg-text-3', '#6b7483');
            // the two songs keep their deck colours down here: A (the singer) and B (the music)
            var acc = cssVar(canvas, '--mm-a', cssVar(canvas, '--lg-accent', '#7aa7ff')), line = cssVar(canvas, '--lg-border', 'rgba(255,255,255,.12)');
            var mus = cssVar(canvas, '--mm-b', fg), accC = cssVar(canvas, '--mm-c', '#2bb89a');
            // time runs left to right in every language: on an Arabic page the canvas inherits rtl, and
            // text drawn from x = 10 ran off the left edge (lane names showed as a bare «A» and «B»)
            try { ctx.direction = 'ltr'; } catch (_) {}
            ctx.clearRect(0, 0, W, H);
            var L = lanes();
            // lane backgrounds
            ctx.fillStyle = 'rgba(127,127,127,.06)';
            ctx.fillRect(0, L.vocal.y, W, L.vocal.h); ctx.fillRect(0, L.music.y, W, L.music.h);
            if (L.vocal2) ctx.fillRect(0, L.vocal2.y, W, L.vocal2.h);
            // bars
            var span = view.t1 - view.t0, pxPerBar = S.bars.length > 1 ? W * (S.bars[1] - S.bars[0]) / span : 50;
            var every = pxPerBar > 42 ? 1 : pxPerBar > 18 ? 4 : pxPerBar > 6 ? 8 : 16;
            ctx.font = '11px system-ui, sans-serif'; ctx.textBaseline = 'middle';
            for (var b = 0; b < S.bars.length; b++) {
                var bx = x(S.bars[b]);
                if (bx < -2 || bx > W + 2) continue;
                var major = b % every === 0;
                ctx.fillStyle = major ? line : 'rgba(127,127,127,.08)';
                ctx.fillRect(Math.round(bx), RULER - (major ? 6 : 3), 1, H - RULER + (major ? 6 : 3));
                if (major) { ctx.fillStyle = dim; ctx.fillText(String(b + 1), Math.round(bx) + 3, RULER / 2); }
            }
            // music — over the span it really sounds: drawn across the whole mashup, a music that ended
            // under the vocal looked as if it went on to the end (bug report 2026-10-06)
            ctx.globalAlpha = 0.8;
            wave(musicPeaks, peakCols, 0, S.music ? S.music.duration : S.duration, L.music, mus);
            ctx.globalAlpha = 1;
            // where the music goes round again: a seam at each repeat
            if (S.seams && S.seams.length) {
                ctx.strokeStyle = mus; ctx.lineWidth = 1; ctx.globalAlpha = 0.75;
                try { ctx.setLineDash([3, 3]); } catch (_) {}
                S.seams.forEach(function (ts) {
                    var sx = Math.round(x(ts)) + 0.5;
                    if (sx < 0 || sx > W) return;
                    ctx.beginPath(); ctx.moveTo(sx, L.music.y); ctx.lineTo(sx, L.music.y + L.music.h); ctx.stroke();
                });
                try { ctx.setLineDash([]); } catch (_) {}
                ctx.globalAlpha = 1;
            }
            // vocal blocks (A, and C on a three-song mashup); the active one wears the thicker edge
            var two = !!L.vocal2;
            [['vocal', vocalPeaks, acc, 'a'], ['vocal2', vocal2Peaks, accC, 'c']].forEach(function (vb) {
                var V = blockOf(vb[0]), ln = L[vb[0]];
                if (!V || !V.buffer || !ln) return;
                var dragging = drag && drag.kind === vb[0], trimming = dragging && drag.part !== 'body';
                var at = V.at + (dragging && !trimming ? drag.dt : 0), dur = V.buffer.duration;
                var xa = x(at), xb = x(at + dur);
                ctx.globalAlpha = dragging ? 0.22 : 0.13;
                ctx.fillStyle = vb[2];
                ctx.fillRect(xa, ln.y, xb - xa, ln.h);
                ctx.globalAlpha = 1;
                ctx.strokeStyle = vb[2]; ctx.lineWidth = dragging || (two && S.active === vb[3]) ? 2 : 1;
                ctx.strokeRect(xa + 0.5, ln.y + 0.5, xb - xa - 1, ln.h - 1);
                wave(vb[1], vb[1] ? vb[1].length / 2 : 0, at, dur, ln, vb[2]);
                if (trimming) {
                    // the edge where it will be: what is cut goes dark, what is drawn out is outlined
                    var ex = x((drag.part === 'trim0' ? at : at + dur) + drag.dt), e0 = drag.part === 'trim0' ? xa : xb;
                    var lo = Math.min(ex, e0), hi = Math.max(ex, e0), cut = drag.part === 'trim0' ? ex > e0 : ex < e0;
                    if (cut) { ctx.globalAlpha = 0.55; ctx.fillStyle = cssVar(canvas, '--bg-primary', '#0b1020'); ctx.fillRect(lo, ln.y, hi - lo, ln.h); ctx.globalAlpha = 1; }
                    else if (hi - lo > 1) { try { ctx.setLineDash([4, 3]); } catch (_) {} ctx.strokeRect(lo + 0.5, ln.y + 0.5, hi - lo - 1, ln.h - 1); try { ctx.setLineDash([]); } catch (_) {} }
                    ctx.fillStyle = vb[2]; ctx.fillRect(Math.round(ex) - 1, ln.y, 3, ln.h);
                }
                // the grips of the block the controls edit: a pill at each edge says it can be trimmed — the page's
                // own background inside, the lane's colour round it, readable over any waveform in either theme
                if (!dragging && (!two || S.active === vb[3]) && xb - xa >= 4 * edgeGrip()) {
                    var gh = Math.min(22, ln.h - 8), gy = Math.round(ln.y + ln.h / 2 - gh / 2);
                    [xa + 2, xb - 8].forEach(function (gx) {
                        gx = Math.round(gx);
                        ctx.fillStyle = cssVar(canvas, '--bg-primary', '#0b1020'); ctx.fillRect(gx, gy, 6, gh);
                        ctx.strokeStyle = vb[2]; ctx.lineWidth = 1.5; ctx.strokeRect(gx + 0.75, gy + 0.75, 4.5, gh - 1.5);
                        ctx.fillStyle = vb[2]; ctx.fillRect(gx + 2, gy + 5, 2, gh - 10);
                    });
                }
            });
            // lane names — which row is whose, without a legend to read
            if (opts.lanes) {
                ctx.font = '600 12px system-ui, sans-serif'; ctx.textBaseline = 'top';
                [[opts.lanes.vocal, L.vocal, acc], [opts.lanes.vocal2, L.vocal2, accC], [opts.lanes.music, L.music, mus]].forEach(function (l) {
                    if (!l[0] || !l[1]) return;
                    var tw = ctx.measureText(l[0]).width + 12;
                    // the page's own background under the name: readable over any waveform, either theme
                    ctx.globalAlpha = 0.86; ctx.fillStyle = cssVar(canvas, '--bg-primary', '#0b1020');
                    ctx.fillRect(4, l[1].y + 4, tw, 18);
                    ctx.globalAlpha = 1; ctx.fillStyle = l[2];
                    ctx.fillText(l[0], 10, l[1].y + 7);
                });
                ctx.textBaseline = 'middle';
            }
            // the mashup's edges: what the file leaves out goes dark over every lane; a grip at each edge on the music
            if (opts.onRange && S.music) {
                var re = rangeEdges(), rd = drag && drag.kind === 'range' ? drag : null;
                if (rd) re[rd.part === 'r0' ? 0 : 1] += rd.dt;
                var shade = cssVar(canvas, '--bg-primary', '#0b1020');
                ctx.globalAlpha = 0.5; ctx.fillStyle = shade;
                var ra = x(re[0]), rb = x(re[1]);
                if (ra > 0) ctx.fillRect(0, RULER, Math.min(W, ra), H - RULER);
                if (rb < W) ctx.fillRect(Math.max(0, rb), RULER, W - Math.max(0, rb), H - RULER);
                ctx.globalAlpha = 1;
                var mh = Math.min(26, L.music.h - 6), my = Math.round(L.music.y + L.music.h / 2 - mh / 2);
                [[ra, 0], [rb, -7]].forEach(function (e) {
                    var gx = Math.round(Math.max(0, Math.min(W - 7, e[0] + e[1])));
                    ctx.fillStyle = shade; ctx.fillRect(gx, my, 7, mh);
                    ctx.strokeStyle = mus; ctx.lineWidth = 1.5; ctx.strokeRect(gx + 0.75, my + 0.75, 5.5, mh - 1.5);
                    ctx.fillStyle = mus; ctx.fillRect(gx + 2.5, my + 5, 2, mh - 10);
                });
            }
            // playhead
            var px = x(ph);
            if (px >= 0 && px <= W) { ctx.fillStyle = cssVar(canvas, '--lg-text', '#fff'); ctx.fillRect(Math.round(px), RULER, 2, H - RULER); }
            // focus ring
            if (document.activeElement === canvas) { ctx.strokeStyle = acc; ctx.lineWidth = 2; ctx.strokeRect(1, 1, W - 2, H - 2); }
        }
        function schedule() { root.cancelAnimationFrame(raf); raf = root.requestAnimationFrame(draw); }

        function syncAria() {
            canvas.setAttribute('aria-valuemin', '1');
            canvas.setAttribute('aria-valuemax', String(Math.max(1, S.bars.length)));
            var sb = S.active === 'c' && S.vocal2 ? S.startBar2 : S.startBar;
            canvas.setAttribute('aria-valuenow', String(sb + 1));
            if (opts.valueText) canvas.setAttribute('aria-valuetext', opts.valueText(sb + 1, S.vocal2 ? S.active : null));
        }

        // ── pointer: drag the vocal (the page snaps it), trim it at an edge, pan the view, click to seek ──
        function cursorFor(h) { return !h ? 'pointer' : h.part === 'body' ? 'grab' : 'ew-resize'; }   // edges and range grips: ew-resize
        function onDown(ev) {
            if (ev.button != null && ev.button !== 0) return;
            var r = canvas.getBoundingClientRect(), px = ev.clientX - r.left, py = ev.clientY - r.top;
            var hit = hitPart(px, py);
            drag = { kind: hit ? hit.kind : 'pan', part: hit ? hit.part : null, x0: px, t0v: view.t0, moved: false, dt: 0, raw: 0, pid: ev.pointerId };
            try { canvas.setPointerCapture(ev.pointerId); } catch (_) {}
            if (hit) {
                canvas.style.cursor = hit.part === 'body' ? 'grabbing' : 'ew-resize';
                // the vocal being moved or trimmed is the one the controls edit
                if (hit.kind !== 'range' && opts.onMove) opts.onMove({ which: hit.kind === 'vocal2' ? 'c' : 'a', dt: 0, final: false });
            }
            ev.preventDefault();
        }
        function onMove(ev) {
            var r = canvas.getBoundingClientRect(), px = ev.clientX - r.left, py = ev.clientY - r.top;
            if (!drag) { canvas.style.cursor = cursorFor(hitPart(px, py)); return; }
            var dpx = px - drag.x0;
            if (Math.abs(dpx) > 3) drag.moved = true;
            var span = view.t1 - view.t0;
            if (drag.kind === 'vocal' || drag.kind === 'vocal2') {
                drag.raw = dpx / W * span;
                var kind = drag.part === 'body' ? 'move' : drag.part, which = drag.kind === 'vocal2' ? 'c' : 'a';
                drag.dt = opts.snap ? opts.snap(drag.raw, which, kind) : drag.raw;
            } else if (drag.kind === 'range') {
                drag.raw = dpx / W * span;
                drag.dt = opts.snap ? opts.snap(drag.raw, null, drag.part) : drag.raw;
            } else if (drag.moved && span < S.duration) {
                var t0 = drag.t0v - dpx / W * span;
                t0 = Math.max(0, Math.min(S.duration - span, t0));
                view.t0 = t0; view.t1 = t0 + span;
            }
            schedule();
        }
        function onUp(ev) {
            if (!drag) return;
            var d = drag; drag = null; canvas.style.cursor = '';
            try { canvas.releasePointerCapture(d.pid); } catch (_) {}
            var which = d.kind === 'vocal2' ? 'c' : 'a', block = d.kind === 'vocal' || d.kind === 'vocal2';
            if (d.kind === 'range' && d.moved && ev.type !== 'pointercancel') { if (opts.onRange) opts.onRange({ edge: d.part === 'r0' ? 0 : 1, dt: d.raw }); }
            else if (block && d.moved && ev.type !== 'pointercancel') {
                if (d.part === 'body') { if (opts.onMove) opts.onMove({ which: which, dt: d.raw, final: true }); }
                else if (opts.onTrim) opts.onTrim({ which: which, edge: d.part === 'trim0' ? 0 : 1, dt: d.raw });
            } else if (!d.moved && ev.type !== 'pointercancel') {
                var r = canvas.getBoundingClientRect();
                // a tap on a vocal block of a three-song mashup picks the vocal the controls then edit
                if (S.vocal2 && block && opts.onSelect) opts.onSelect(which);
                if (opts.onSeek) opts.onSeek(Math.max(0, Math.min(S.duration, tAt(ev.clientX - r.left))));
            }
            schedule();
        }
        function onWheel(ev) {
            if (!ev.ctrlKey && Math.abs(ev.deltaY) < Math.abs(ev.deltaX)) {
                var span = view.t1 - view.t0, dt = ev.deltaX / W * span;
                view.t0 = Math.max(0, Math.min(S.duration - span, view.t0 + dt)); view.t1 = view.t0 + span;
                ev.preventDefault(); schedule(); return;
            }
            if (!ev.ctrlKey && !ev.metaKey) return;          // plain wheel scrolls the page
            var r = canvas.getBoundingClientRect();
            zoomAt(ev.deltaY < 0 ? 1 : -1, tAt(ev.clientX - r.left));
            ev.preventDefault();
        }
        function zoomAt(dir, center) {
            var span = view.t1 - view.t0, ns = Math.max(4, Math.min(S.duration, dir > 0 ? span / 1.6 : span * 1.6));
            var c = center != null ? center : (view.t0 + view.t1) / 2, f = (c - view.t0) / span;
            view.t0 = Math.max(0, Math.min(S.duration - ns, c - f * ns)); view.t1 = view.t0 + ns;
            buildPeaks(); schedule();
        }
        // ←/→ a bar, Shift a beat, Alt 10 ms — time runs left to right in every language
        function onKey(ev) {
            var k = ev.key;
            if (k !== 'ArrowLeft' && k !== 'ArrowRight') return;
            var dir = k === 'ArrowRight' ? 1 : -1, w = S.active === 'c' && S.vocal2 ? 'c' : 'a';
            if (opts.onKey) opts.onKey(ev.altKey ? { which: w, bars: 0, beats: 0, fine: dir * 0.01 }
                : ev.shiftKey ? { which: w, bars: 0, beats: dir, fine: 0 } : { which: w, bars: dir, beats: 0, fine: 0 });
            ev.preventDefault();
        }

        canvas.addEventListener('pointerdown', onDown);
        canvas.addEventListener('pointermove', onMove);
        canvas.addEventListener('pointerup', onUp);
        canvas.addEventListener('pointercancel', onUp);
        canvas.addEventListener('wheel', onWheel, { passive: false });
        canvas.addEventListener('keydown', onKey);
        canvas.addEventListener('focus', schedule); canvas.addEventListener('blur', schedule);
        var ro = root.ResizeObserver ? new ResizeObserver(resize) : null;
        if (ro) ro.observe(canvas); else root.addEventListener('resize', resize);

        return {
            set: function (s) {
                var hadDur = S.duration;
                Object.assign(S, s);
                if (!hadDur || s.duration && Math.abs(s.duration - hadDur) > 0.01 || view.t1 > S.duration) { view.t0 = 0; view.t1 = S.duration; }
                peakCols = 0; buildPeaks(); syncAria(); resize(); draw();
            },
            playhead: function (t) {
                ph = t;
                // keep the playhead in view while zoomed (a view scroll, not a page scroll)
                var span = view.t1 - view.t0;
                if (span < S.duration && (t > view.t1 - span * 0.1 || t < view.t0)) { view.t0 = Math.max(0, Math.min(S.duration - span, t - span * 0.2)); view.t1 = view.t0 + span; }
                schedule();
            },
            zoom: function (dir) { zoomAt(dir, ph); },
            redraw: schedule,
            destroy: function () { if (ro) ro.disconnect(); root.cancelAnimationFrame(raf); }
        };
    }

    root.MashupTimeline = { create: create };
})(typeof window !== 'undefined' ? window : self);
